"use client";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaCar, FaBus, FaTruck } from "react-icons/fa";
import { GiForklift } from "react-icons/gi";
import "./TopFilterBar.css";

export default function TopFilterBar() {
  const navigate = useNavigate();

  // makers column
  const makers = [
    "TOYOTA", "MITSUBISHI", "NISSAN", "HONDA", "MAZDA",
    "SUBARU", "BMW", "HINO", "SUZUKI", "AUDI", "MERCEDES",
    "VOLKSWAGEN", "ISUZU", "KOMATSU", "LEXUS", "DAIHATSU"
  ];

  const handleMakerClick = (maker) => {
    navigate(`/search?Make=${encodeURIComponent(maker)}`);
  };

  const getLogoPaths = (maker) => {
    const variations = [
      maker.toLowerCase(),
      maker.toUpperCase(),
      maker.charAt(0).toUpperCase() + maker.slice(1).toLowerCase()
    ];
    const extensions = ["png", "jpg", "jpeg", "svg", "webp"];
    const paths = [];
    variations.forEach(variation => {
      extensions.forEach(ext => {
        paths.push(`/manufacturer_logo/${variation}.${ext}`);
      });
    });
    return paths;
  };

  const tryLoadLogo = (maker, imgElement, fallbackElement) => {
    const paths = getLogoPaths(maker);
    let currentIndex = 0;

    const tryNextPath = () => {
      if (currentIndex >= paths.length) {
        imgElement.style.display = "none";
        fallbackElement.style.display = "inline-block";
        return;
      }
      const currentPath = paths[currentIndex];
      imgElement.onload = () => {
        imgElement.style.display = "block";
        fallbackElement.style.display = "none";
      };
      imgElement.onerror = () => {
        currentIndex++;
        tryNextPath();
      };
      imgElement.src = currentPath;
    };

    tryNextPath();
  };

  const ManufacturerLogo = ({ maker }) => {
    const imgRef = React.useRef(null);
    const fallbackRef = React.useRef(null);

    useEffect(() => {
      if (imgRef.current && fallbackRef.current) {
        tryLoadLogo(maker, imgRef.current, fallbackRef.current);
      }
    }, [maker]);

    return (
      <div className="icon-container">
        <img ref={imgRef} alt={`${maker} logo`} className="manufacturer-logo" style={{ display: "none" }} />
        <FaCar ref={fallbackRef} className="icon fallback-icon" style={{ display: "inline-block" }} />
      </div>
    );
  };

  // shapes column
  const shapes = [
    "COUPE", "SEDAN", "HATCHBACK", "WAGON", "SUV", "BUS",
    "VAN", "TRUCK", "FORKLIFT", "Pick up Truck"
  ];

  const shapeIcons = {
    COUPE: <FaCar />,
    SEDAN: <FaCar />,
    HATCHBACK: <FaCar />,
    WAGON: <FaCar />,
    SUV: <FaCar />,
    BUS: <FaBus />,
    VAN: <FaBus />,
    TRUCK: <FaTruck />,
    FORKLIFT: <GiForklift />,
    "Pick up Truck": <FaTruck />
  };

  const handleShapeClick = (shape) => {
    navigate(`/search?Category=${encodeURIComponent(shape)}`);
  };

  // filters column
  const [name, setName] = useState("");
  const [make, setMake] = useState("");
  const [record_ID, setRecordID] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [shapeFilter, setShapeFilter] = useState("");

  const [makeOptions, setMakeOptions] = useState([]);
  const [shapeOptions, setShapeOptions] = useState([]);

  useEffect(() => {
    const fetchFilterCounts = async () => {
      try {
        const res = await axios.get("https://automotive-backend-production.up.railway.app/inventory/filters");
        setMakeOptions(res.data.makes);
        setShapeOptions(res.data.shapes);
      } catch (err) {
        console.error("Failed to load filter options", err);
      }
    };
    fetchFilterCounts();
  }, []);

  const handleSearch = () => {
    if (record_ID && !/^[a-zA-Z0-9-]+$/.test(record_ID)) {
      alert("Record ID should be alphanumeric.");
      return;
    }
    if (name && !/^[a-zA-Z\s]+$/.test(name)) {
      alert("Name should only contain letters and spaces.");
      return;
    }
    if ((priceMin && isNaN(priceMin)) || (priceMax && isNaN(priceMax))) {
      alert("Prices must be numeric.");
      return;
    }
    if ((priceMin && !priceMax) || (!priceMin && priceMax)) {
      alert("Provide both Min and Max price.");
      return;
    }
    if (priceMin && priceMax && Number(priceMin) > Number(priceMax)) {
      alert("Min cannot exceed Max.");
      return;
    }

    const params = new URLSearchParams();
    if (name) params.append("Name", name);
    if (make) params.append("Make", make);
    if (shapeFilter) params.append("Category", shapeFilter);
    if (record_ID) params.append("RecordNumber", record_ID);
    if (priceMin && priceMax) {
      params.append("Price", `${priceMin}-${priceMax}`);
    }

    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="container my-4">
      <div className="top-filter-bar">
        <div className="row g-3">
          {/* makers */}
          <div className="col-lg-4">
            <h4>Search By Makers</h4>
            <ul className="search-list">
              {makers.map((maker, index) => (
                <li key={index} className="search-item" onClick={() => handleMakerClick(maker)}>
                  <ManufacturerLogo maker={maker} />
                  <span>{maker}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* shapes */}
          <div className="col-lg-4">
            <h4>Search By Shapes</h4>
            <ul className="shapes-list">
              {shapes.map((shape, index) => (
                <li key={index} className="shape-item" onClick={() => handleShapeClick(shape)}>
                  <div className="shape-icon-container">{shapeIcons[shape]}</div>
                  <span>{shape}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* advanced filters */}
          <div className="col-lg-4">
            <h4>Advanced Filters</h4>
            <div className="search-fields">
              <input
                type="text"
                placeholder="Record ID"
                className="search-input"
                value={record_ID}
                onChange={(e) => setRecordID(e.target.value)}
              />
              <input
                type="text"
                placeholder="Vehicle Name"
                className="search-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <select
                className="search-input"
                value={make}
                onChange={(e) => setMake(e.target.value)}
              >
                <option value="">Make (ALL)</option>
                {makeOptions.map((item, idx) => (
                  <option key={idx} value={item._id}>{item._id} ({item.count})</option>
                ))}
              </select>
              <div className="price-range d-flex gap-2">
                <input
                  type="text"
                  placeholder="Price Min"
                  className="search-input"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Price Max"
                  className="search-input"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                />
              </div>
              <select
                className="search-input"
                value={shapeFilter}
                onChange={(e) => setShapeFilter(e.target.value)}
              >
                <option value="">Shape (ALL)</option>
                {shapeOptions.map((item, idx) => (
                  <option key={idx} value={item._id}>{item._id} ({item.count})</option>
                ))}
              </select>
              <button className="search-button mt-2" onClick={handleSearch}>
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
